import { ldb, state } from '@codesuma/baseline'
import { BUILT_IN_ADJUSTMENTS } from '../configs/hijri-adjustments'
import { OBSERVED_ADJUSTMENTS } from '../configs/hijri-observed-adjustments'
import { HIJRI_ENDPOINT } from '../configs/app'
import { IAdjustmentPayload, validate, merge } from './hijri-payload'
import { setHijriAdjustments } from './hijri'

// Delivers moon-sighting corrections without an app release. Iran announces the
// start of a month the evening before, so a wrong Eid or Ashura can't wait for
// a store review.
//
// Priority, highest first:
//   1. downloaded  — the newest payload fetched from HIJRI_ENDPOINT
//   2. saved       — that payload, persisted, so it still applies offline
//   3. built-in    — compiled into the app: the observed رمضان/شوال record in
//                   configs/hijri-observed-adjustments.ts, under the hand-kept
//                   list in configs/hijri-adjustments.ts
//
// 1 and 2 are the same list: a fetch only takes effect by being saved, so the
// tiers collapse to "saved list over built-in list". Merging is per month, so
// a downloaded entry replaces the built-in shift for that month alone and
// leaves every other month on its built-in value.

interface IStored extends IAdjustmentPayload {
    checked: number // epoch ms of the last completed check, for the throttle
}

const KEY = 'hijri-adjustments'
const CHECK_INTERVAL = 6 * 60 * 60 * 1000

function read(): IStored | null {
    const stored = ldb.get(KEY)
    const valid = validate(stored)
    if (!valid) return null
    const { checked } = stored as IStored
    return { ...valid, checked: Number.isFinite(checked) ? checked : 0 }
}

// The two compiled-in lists collapse to one base at module load: the observed
// record is the floor, and the hand-kept list wins any month they share.
const BUILT_IN = merge(OBSERVED_ADJUSTMENTS, BUILT_IN_ADJUSTMENTS)

function apply(saved: IAdjustmentPayload | null) {
    setHijriAdjustments(saved ? merge(BUILT_IN, saved.adjustments) : BUILT_IN)
}

// One conditional GET of a small static file. `cache: 'no-cache'` makes the
// platform revalidate using its own ETag / If-Modified-Since, so an unchanged
// file costs a 304 with no body — no hand-rolled ETag bookkeeping. Raw fetch
// rather than baseline's http because that one sends credentials, which a
// static host answering `Access-Control-Allow-Origin: *` cannot accept.
async function check(stored: IStored | null) {
    if (!HIJRI_ENDPOINT) return
    if (navigator.onLine === false) return
    if (stored && Date.now() - stored.checked < CHECK_INTERVAL) return

    try {
        const res = await fetch(HIJRI_ENDPOINT, { cache: 'no-cache' })
        if (!res.ok) return
        const payload = validate(await res.json())
        const checked = Date.now()
        // A stale mirror (or a rejected payload) must not roll a correction
        // back, but it still counts as a completed check for the throttle.
        if (!payload || (stored && payload.version <= stored.version)) {
            if (stored) ldb.set(KEY, { ...stored, checked })
            return
        }
        const next: IStored = { ...payload, checked }
        ldb.set(KEY, next)
        apply(payload)
        // Slides render hijri day numbers and the occasions anchored to them,
        // so they have to be rebuilt with the new shifts.
        state.set('hijri', payload.version)
    } catch {
        // Offline, DNS failure, malformed JSON: keep whatever is already
        // applied and retry on the next launch.
    }
}

// Applies the saved corrections synchronously — call before the first render —
// then checks for newer ones in the background.
function init() {
    const stored = read()
    apply(stored)
    check(stored)
}

export default { init }
